import React, { Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ShoppingBagIcon, PackageIcon, MessageCircleIcon } from 'lucide-react';
import type { CartItem } from '../hooks/useCart';
interface CartSidebarProps {
  isOpen: boolean;
  cart: CartItem[];
  totalPrice: number;
  onClose: () => void;
  onRemoveItem: (index: number) => void;
  onChangeQty: (index: number, delta: number) => void;
}
function formatPrice(price: number): string {
  return '$' + price.toLocaleString('es-AR');
}
export function CartSidebar({
  isOpen,
  cart,
  totalPrice,
  onClose,
  onRemoveItem,
  onChangeQty
}: CartSidebarProps) {
  function buildWhatsAppMessage(): string {
    let message = '*RÔA IMPORTED — Nuevo Pedido*\n\n';
    let total = 0;
    cart.forEach(item => {
      const lineTotal = item.price * item.qty;
      message += `• ${item.brand} - ${item.name}\n`;
      message += `  Cantidad: ${item.qty} | ${formatPrice(lineTotal)} ARS\n\n`;
      total += lineTotal;
    });
    message += '━━━━━━━━━━━━━━━\n';
    message += `*TOTAL: ${formatPrice(total)} ARS*\n\n`;
    message += 'Por favor confirmar disponibilidad y envío.';
    return message;
  }
  function handleWhatsApp() {
    if (cart.length === 0) return;
    const phone = '5492235000000';
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(buildWhatsAppMessage())}`;
    window.open(url, '_blank');
  }
  return <AnimatePresence data-id="element-8">
      {isOpen && <>
          {/* Overlay */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="fixed inset-0 z-[200] bg-black/50" onClick={onClose} data-id="element-9" />

          {/* Sidebar */}
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'tween',
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94]
      }} className="fixed right-0 top-0 z-[300] flex h-screen w-full flex-col bg-white shadow-[-10px_0_40px_rgba(0,0,0,0.15)] md:w-[420px]" data-id="element-10">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-[#f0f0f0] px-[25px] pb-5 pt-[25px]" data-id="element-11">
              <h3 className="text-[14px] font-bold uppercase tracking-[4px]" data-id="element-12">
                Mi Carrito
              </h3>
              <button onClick={onClose} className="bg-transparent text-[22px] leading-none text-[#1a1a1a] transition-colors hover:text-[#888]" aria-label="Cerrar carrito" data-id="element-13">
                <XIcon className="h-5 w-5" data-id="element-14" />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto px-[25px] py-5" data-id="element-15">
              {cart.length === 0 ? <div className="px-5 py-[60px] text-center text-[#888]" data-id="element-16">
                  <div className="mb-[15px] flex justify-center" data-id="element-17">
                    <ShoppingBagIcon className="h-12 w-12 text-[#ccc]" data-id="element-18" />
                  </div>
                  <p className="text-[12px] uppercase tracking-[2px]" data-id="element-19">
                    Tu carrito está vacío
                  </p>
                </div> : cart.map((item, index) => <div key={`${item.id}-${index}`} className="relative flex gap-[15px] border-b border-[#f5f5f5] py-[15px]" data-id="element-20">
                    <img src={item.image} alt={item.name} className="h-[90px] w-20 flex-shrink-0 bg-[#f0ece6] object-cover" data-id="element-21" />
                    <div className="flex-1" data-id="element-22">
                      <div className="text-[9px] uppercase tracking-[2px] text-[#888]" data-id="element-23">
                        {item.brand}
                      </div>
                      <div className="my-[3px] text-[13px] font-semibold" data-id="element-24">
                        {item.name}
                      </div>
                      <div className="text-[15px] font-bold" data-id="element-25">
                        {formatPrice(item.price * item.qty)}
                      </div>
                      <div className="mt-2 flex items-center gap-3" data-id="element-26">
                        <button onClick={() => onChangeQty(index, -1)} className="flex h-[26px] w-[26px] items-center justify-center border border-[#ddd] bg-transparent text-[14px] transition-all duration-200 hover:border-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white" aria-label="Disminuir cantidad" data-id="element-27">
                          −
                        </button>
                        <span className="min-w-[20px] text-center text-[13px] font-semibold" data-id="element-28">
                          {item.qty}
                        </span>
                        <button onClick={() => onChangeQty(index, 1)} className="flex h-[26px] w-[26px] items-center justify-center border border-[#ddd] bg-transparent text-[14px] transition-all duration-200 hover:border-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white" aria-label="Aumentar cantidad" data-id="element-29">
                          +
                        </button>
                      </div>
                    </div>
                    <button onClick={() => onRemoveItem(index)} className="absolute right-0 top-[15px] bg-transparent text-[16px] text-[#bbb] transition-colors duration-200 hover:text-[#c1121f]" aria-label={`Eliminar ${item.name}`} data-id="element-30">
                      ✕
                    </button>
                  </div>)}
            </div>

            {/* Footer */}
            <div className="border-t border-[#f0f0f0] px-[25px] py-5" data-id="element-31">
              <div className="mb-2 flex items-center justify-between" data-id="element-32">
                <span className="text-[11px] uppercase tracking-[2px] text-[#888]" data-id="element-33">
                  Total
                </span>
                <span className="text-[20px] font-bold" data-id="element-34">
                  {totalPrice > 0 ? `${formatPrice(totalPrice)} ARS` : '$0'}
                </span>
              </div>
              <p className="mb-5 text-[10px] tracking-[1px] text-[#aaa]" data-id="element-35">
                <span className="flex items-center justify-center gap-1.5" data-id="element-36">
                  <PackageIcon className="h-3 w-3" data-id="element-37" /> Envío a coordinar por
                  WhatsApp
                </span>
              </p>
              <button onClick={handleWhatsApp} className="w-full bg-[#1a1a1a] py-[18px] text-[12px] uppercase tracking-[4px] text-white transition-colors duration-300 hover:bg-[#333]" data-id="element-38">
                Finalizar Pedido
              </button>
              <button onClick={handleWhatsApp} className="mt-[10px] flex w-full items-center justify-center gap-2 bg-[#25D366] py-[14px] text-[12px] uppercase tracking-[3px] text-white transition-colors duration-300 hover:bg-[#128C7E]" data-id="element-39">
                <MessageCircleIcon className="h-4 w-4" data-id="element-40" /> Pedir por WhatsApp
              </button>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}